// The work a new space costs (docs/adr/0003-mass-created-spaces.md; the server's src/core/work.xq): a
// number whose 8 bytes, little-endian, after the server's 16 bytes of challenge make a SHA-256 that begins
// with `bits` zero bits — 2^bits tries on average, a fraction of a second for the 18 bits the server asks
// of a person. The page looks in a worker (worker/work.ts) and stays quick meanwhile. The hash is of one
// message only — one block of SHA-256 — and is computed here without allocating anything per try, in
// 32-bit integers (Int32Array, `| 0` after every sum): numbers past 2^31 would make V8 count in floats,
// twenty times slower.

const K = new Int32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** SHA-256's first state. */
const IV = new Int32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);

/** Tries the page makes at a time when it has to do the work itself, between which it answers. */
const SLICE = 50000;

/** The zero bits SHA-256 of `challenge` (16 bytes) and `nonce` (8 bytes, little-endian) begins with. */
export function zerosOf(challenge: Uint8Array, nonce: number): number {
  return zeros(block(challenge), nonce);
}

/** The first number from `from` on, of `count` at most, that does the work of `bits` bits for
 * `challenge`; -1 if none of them does. */
export function solve(challenge: Uint8Array, bits: number, from = 0, count = Number.MAX_SAFE_INTEGER): number {
  const w = block(challenge);
  const end = Math.min(from + count, Number.MAX_SAFE_INTEGER);
  for (let n = from; n < end; n++) if (zeros(w, n) >= bits) return n;
  return -1;
}

/** The number that does the work, found by a worker, so that the page stays quick; on the page itself,
 * a slice at a time, where a worker does not start. */
export function prove(challenge: Uint8Array, bits: number): Promise<number> {
  return new Promise((resolve) => {
    let w: Worker;
    try {
      w = new Worker("/work.js");
    } catch {
      return here(challenge, bits, 0, resolve);
    }
    w.onmessage = (e: MessageEvent<number>) => {
      w.terminate();
      resolve(e.data);
    };
    w.onerror = () => {
      w.terminate();
      here(challenge, bits, 0, resolve);
    };
    w.postMessage({ challenge, bits });
  });
}

/** The work done on the page: `SLICE` tries at a time, the page answering between them. */
function here(challenge: Uint8Array, bits: number, from: number, done: (n: number) => void): void {
  const n = solve(challenge, bits, from, SLICE);
  if (n >= 0) done(n);
  else setTimeout(() => here(challenge, bits, from + SLICE, done), 0);
}

/** The block of SHA-256 for `challenge` and the 64 words of its schedule: the challenge's 16 bytes as
 * its first four words, big-endian, as SHA-256 reads them, and the padding of a message of 24 bytes — a
 * bit after it, then its length in bits. The number's words, 4 and 5, each try sets. */
function block(challenge: Uint8Array): Int32Array {
  if (challenge.length !== 16) throw new Error("a challenge of work is 16 bytes");
  const w = new Int32Array(64);
  for (let i = 0; i < 4; i++)
    w[i] = (challenge[4 * i] << 24) | (challenge[4 * i + 1] << 16) | (challenge[4 * i + 2] << 8) | challenge[4 * i + 3];
  w[6] = 0x80000000 | 0;
  w[15] = 192;
  return w;
}

/** A 32-bit number's bytes the other way round: little-endian bytes read as SHA-256's big-endian word. */
function swapped(x: number): number {
  return ((x & 0xff) << 24) | ((x & 0xff00) << 8) | ((x >>> 8) & 0xff00) | (x >>> 24);
}

/** The zero bits the hash of block `w` with number `nonce` begins with. */
function zeros(w: Int32Array, nonce: number): number {
  w[4] = swapped(nonce >>> 0);
  w[5] = swapped((nonce / 4294967296) >>> 0);
  for (let i = 16; i < 64; i++) {
    let u = w[i - 2];
    const s1 = ((u >>> 17) | (u << 15)) ^ ((u >>> 19) | (u << 13)) ^ (u >>> 10);
    u = w[i - 15];
    const s0 = ((u >>> 7) | (u << 25)) ^ ((u >>> 18) | (u << 14)) ^ (u >>> 3);
    w[i] = (((s1 + w[i - 7]) | 0) + ((s0 + w[i - 16]) | 0)) | 0;
  }
  let a = IV[0], b = IV[1], c = IV[2], d = IV[3], e = IV[4], f = IV[5], g = IV[6], h = IV[7];
  for (let i = 0; i < 64; i++) {
    const s1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
    const t1 = (((s1 + ((e & f) ^ (~e & g))) | 0) + ((h + ((K[i] + w[i]) | 0)) | 0)) | 0;
    const s0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
    const t2 = (s0 + ((a & b) ^ (a & c) ^ (b & c))) | 0;
    h = g;
    g = f;
    f = e;
    e = (d + t1) | 0;
    d = c;
    c = b;
    b = a;
    a = (t1 + t2) | 0;
  }
  const h0 = (IV[0] + a) | 0;
  if (h0 !== 0) return Math.clz32(h0);
  const h1 = (IV[1] + b) | 0;
  return h1 !== 0 ? 32 + Math.clz32(h1) : 64;
}
